import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api";
import { REFETCH_INTERVAL_MS as REFETCH_MS } from "../lib/constants";
import {
  timeRangeKey,
  timeRangeQuery,
  type TimeRange,
} from "../lib/timeRange";
import type {
  PageResponse,
  ProblemCamera,
  ProblemZone,
  VideoIncident,
  ZoneUptime,
} from "../types/videoHistory";

/** 문제 많은 구역 — 총 지속 내림차순, 페이지네이션. 기간은 상대(N일)/절대(from~to) 모두 지원. */
export function useTopProblemZones(range: TimeRange, page = 0, size = 20) {
  return useQuery<PageResponse<ProblemZone>>({
    queryKey: ["video-history", "top-problem-zones", timeRangeKey(range), page, size],
    queryFn: ({ signal }) =>
      apiGet<PageResponse<ProblemZone>>(
        `/api/video-history/top-problem-zones?${timeRangeQuery(range)}&page=${page}&size=${size}`,
        signal ? { signal } : undefined,
      ),
    refetchInterval: REFETCH_MS,
    placeholderData: (prev) => prev,
  });
}

/** 문제 많은 카메라 — 한 incident 의 cameras 를 펼쳐 카메라별로 집계, 총 지속 내림차순. */
export function useTopProblemCameras(range: TimeRange, page = 0, size = 20) {
  return useQuery<PageResponse<ProblemCamera>>({
    queryKey: ["video-history", "top-problem-cameras", timeRangeKey(range), page, size],
    queryFn: ({ signal }) =>
      apiGet<PageResponse<ProblemCamera>>(
        `/api/video-history/top-problem-cameras?${timeRangeQuery(range)}&page=${page}&size=${size}`,
        signal ? { signal } : undefined,
      ),
    refetchInterval: REFETCH_MS,
    placeholderData: (prev) => prev,
  });
}

/** 구역별 일별 uptime (막대그래프). */
export function useUptime(days = 90) {
  return useQuery<ZoneUptime[]>({
    queryKey: ["video-history", "uptime", days],
    queryFn: ({ signal }) =>
      apiGet<ZoneUptime[]>(
        `/api/video-history/uptime?days=${days}`,
        signal ? { signal } : undefined,
      ),
    refetchInterval: REFETCH_MS,
    placeholderData: (prev) => prev,
  });
}

/** 최근 incident 타임라인. */
export function useRecentIncidents(limit = 20) {
  return useQuery<VideoIncident[]>({
    queryKey: ["video-history", "incidents", limit],
    queryFn: ({ signal }) =>
      apiGet<VideoIncident[]>(
        `/api/video-history/incidents?limit=${limit}`,
        signal ? { signal } : undefined,
      ),
    refetchInterval: REFETCH_MS,
    placeholderData: (prev) => prev,
  });
}
